/**
 * Catalogue menu restaurant — sections QuickSale & Inventaire plats.
 * Groupes : Fast-food · Plats · Entrées · Boissons · Café · Desserts · Menus.
 */

export const RESTAURANT_MENU_CATEGORY = 'restaurant-menu';

/** Select value for free-text menu section / type in dish forms. */
export const RESTAURANT_MENU_CUSTOM_VALUE = '__custom__';

/**
 * Sections principales du menu (subcategory / menuSection sur les plats).
 * Ordre = affichage POS & formulaires.
 */
export const RESTAURANT_MENU_SECTIONS = [
  // —— Fast-food ——
  { key: 'sandwiches', name: 'Sandwiches', nameAr: 'ساندويتش', group: 'fast-food' },
  { key: 'shawarma', name: 'Shawarma', nameAr: 'شاورما', group: 'fast-food' },
  { key: 'tacos', name: 'Tacos', nameAr: 'تاكوس', group: 'fast-food' },
  { key: 'burgers', name: 'Burgers', nameAr: 'برغر', group: 'fast-food' },
  { key: 'pizza', name: 'Pizza', nameAr: 'بيتزا', group: 'fast-food' },
  // —— Plats ——
  { key: 'grill', name: 'Grillades', nameAr: 'مشويات', group: 'mains' },
  { key: 'pasta', name: 'Pâtes', nameAr: 'معكرونة', group: 'mains' },
  { key: 'seafood', name: 'Poissons & Fruits de mer', nameAr: 'أسماك ومأكولات بحرية', group: 'mains' },
  { key: 'mauritanian', name: 'Plats locaux', nameAr: 'أطباق محلية', group: 'mains' },
  { key: 'plates', name: 'Assiettes', nameAr: 'أطباق', group: 'mains' },
  // —— Entrées & accompagnements ——
  { key: 'starters', name: 'Entrées', nameAr: 'مقبلات', group: 'sides' },
  { key: 'salads', name: 'Salades', nameAr: 'سلطات', group: 'sides' },
  { key: 'soup', name: 'Soupes', nameAr: 'شوربات', group: 'sides' },
  { key: 'sides', name: 'Accompagnements', nameAr: 'جانبية', group: 'sides' },
  { key: 'breakfast', name: 'Petit-déjeuner', nameAr: 'فطور', group: 'sides' },
  // —— Boissons froides ——
  { key: 'juices', name: 'Jus', nameAr: 'عصائر', group: 'drinks' },
  { key: 'soft-drinks', name: 'Boissons gazeuses', nameAr: 'مشروبات غازية', group: 'drinks' },
  { key: 'smoothies', name: 'Smoothies & Milkshakes', nameAr: 'سموذي وميلك شيك', group: 'drinks' },
  { key: 'water', name: 'Eau', nameAr: 'ماء', group: 'drinks' },
  // Legacy: anciens plats « Boissons menu »
  { key: 'menu-drinks', name: 'Boissons (général)', nameAr: 'مشروبات عامة', group: 'drinks' },
  // —— Café ——
  { key: 'cafe', name: 'Café & Thé', nameAr: 'قهوة وشاي', group: 'cafe' },
  // —— Desserts & formules ——
  { key: 'desserts', name: 'Desserts', nameAr: 'حلويات', group: 'desserts' },
  { key: 'combo-menu', name: 'Menus / Formules', nameAr: 'وجبات كاملة', group: 'desserts' },
  { key: 'other', name: 'Autres', nameAr: 'أخرى', group: 'other' }
];

/** Libellés des groupes (optgroup dans Ajouter Plat). */
export const RESTAURANT_MENU_SECTION_GROUPS = [
  { key: 'fast-food', name: 'Fast-food', nameAr: 'وجبات سريعة' },
  { key: 'mains', name: 'Plats', nameAr: 'أطباق رئيسية' },
  { key: 'sides', name: 'Entrées & accompagnements', nameAr: 'مقبلات وجانبية' },
  { key: 'drinks', name: 'Boissons froides', nameAr: 'مشروبات باردة' },
  { key: 'cafe', name: 'Café', nameAr: 'مقهى' },
  { key: 'desserts', name: 'Desserts & menus', nameAr: 'حلويات وقوائم' },
  { key: 'other', name: 'Autre', nameAr: 'أخرى' }
];

/** Sections comptées comme boissons (rapports restaurant). */
export const RESTAURANT_DRINK_SECTION_KEYS = new Set([
  'juices',
  'soft-drinks',
  'smoothies',
  'water',
  'menu-drinks',
  'cafe'
]);

/** Types optionnels par section (clé type sur le plat). */
export const RESTAURANT_MENU_TYPES_BY_SECTION = {
  sandwiches: [
    { key: 'club', name: 'Club', nameAr: 'كلوب' },
    { key: 'panini', name: 'Panini', nameAr: 'بانيني' },
    { key: 'wrap', name: 'Wrap', nameAr: 'راب' },
    { key: 'toast', name: 'Toast', nameAr: 'توست' },
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'beef', name: 'Viande', nameAr: 'لحم' },
    { key: 'tuna', name: 'Thon', nameAr: 'تونة' },
    { key: 'vegetarian', name: 'Végétarien', nameAr: 'نباتي' },
    { key: 'classic', name: 'Classique', nameAr: 'كلاسيكي' }
  ],
  shawarma: [
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'beef', name: 'Bœuf', nameAr: 'لحم بقر' },
    { key: 'mixed', name: 'Mixte', nameAr: 'مختلط' },
    { key: 'plate', name: 'Assiette', nameAr: 'طبق' },
    { key: 'sandwich', name: 'Sandwich', nameAr: 'ساندويتش' }
  ],
  tacos: [
    { key: 'beef', name: 'Bœuf', nameAr: 'لحم' },
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'fish', name: 'Poisson', nameAr: 'سمك' },
    { key: 'merged', name: 'Merguez', nameAr: 'مرگز' },
    { key: 'vegetarian', name: 'Végétarien', nameAr: 'نباتي' }
  ],
  burgers: [
    { key: 'classic', name: 'Classique', nameAr: 'كلاسيكي' },
    { key: 'cheese', name: 'Cheese Burger', nameAr: 'تشيز برغر' },
    { key: 'beef', name: 'Bœuf', nameAr: 'لحم بقر' },
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'fish', name: 'Poisson', nameAr: 'سمك' },
    { key: 'double', name: 'Double', nameAr: 'دبل' },
    { key: 'triple', name: 'Triple', nameAr: 'تربل' },
    { key: 'vegetarian', name: 'Végétarien', nameAr: 'نباتي' },
    { key: 'special', name: 'Spécial maison', nameAr: 'خاص' }
  ],
  pizza: [
    { key: 'margherita', name: 'Margherita', nameAr: 'مارغريتا' },
    { key: 'pepperoni', name: 'Pepperoni', nameAr: 'بيبروني' },
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'vegetarian', name: 'Végétarienne', nameAr: 'نباتية' },
    { key: 'seafood', name: 'Fruits de mer', nameAr: 'مأكولات بحرية' },
    { key: '4-fromages', name: '4 Fromages', nameAr: 'أربعة أجبان' },
    { key: 'royale', name: 'Royale', nameAr: 'رويال' },
    { key: 'special', name: 'Spéciale', nameAr: 'خاصة' }
  ],
  grill: [
    { key: 'brochette', name: 'Brochette', nameAr: 'سيخ' },
    { key: 'mixed-grill', name: 'Grill mixte', nameAr: 'مشكل مشاوي' },
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'lamb', name: 'Agneau', nameAr: 'لحم غنم' },
    { key: 'beef', name: 'Bœuf', nameAr: 'لحم بقر' },
    { key: 'kebab', name: 'Kebab', nameAr: 'كباب' }
  ],
  pasta: [
    { key: 'bolognese', name: 'Bolognaise', nameAr: 'بولونيز' },
    { key: 'carbonara', name: 'Carbonara', nameAr: 'كاربونارا' },
    { key: 'pesto', name: 'Pesto', nameAr: 'بيستو' },
    { key: 'seafood-pasta', name: 'Fruits de mer', nameAr: 'مأكولات بحرية' },
    { key: 'chicken-pasta', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'vegetarian', name: 'Végétariennes', nameAr: 'نباتية' }
  ],
  seafood: [
    { key: 'grilled-fish', name: 'Poisson grillé', nameAr: 'سمك مشوي' },
    { key: 'fried-fish', name: 'Poisson frit', nameAr: 'سمك مقلي' },
    { key: 'shrimp', name: 'Crevettes', nameAr: 'روبيان' },
    { key: 'calamari', name: 'Calamars', nameAr: 'حبار' },
    { key: 'seafood-plate', name: 'Assiette mer', nameAr: 'طبق بحري' }
  ],
  mauritanian: [
    { key: 'thieb', name: 'Thieboudienne', nameAr: 'ثيابوديين' },
    { key: 'yassa', name: 'Yassa', nameAr: 'ياسا' },
    { key: 'mafé', name: 'Mafé', nameAr: 'مافيه' },
    { key: 'couscous', name: 'Couscous', nameAr: 'كسكس' },
    { key: 'mechoui', name: 'Méchoui', nameAr: 'مشوي' },
    { key: 'riz-sauce', name: 'Riz sauce', nameAr: 'أرز بالصلصة' }
  ],
  plates: [
    { key: 'chicken-plate', name: 'Assiette poulet', nameAr: 'طبق دجاج' },
    { key: 'meat-plate', name: 'Assiette viande', nameAr: 'طبق لحم' },
    { key: 'fish-plate', name: 'Assiette poisson', nameAr: 'طبق سمك' },
    { key: 'mixed-plate', name: 'Assiette mixte', nameAr: 'طبق مشكل' },
    { key: 'rice-plate', name: 'Assiette riz', nameAr: 'طبق أرز' }
  ],
  starters: [
    { key: 'brick', name: 'Brick', nameAr: 'بريك' },
    { key: 'samosa', name: 'Samoussa', nameAr: 'سمبوسة' },
    { key: 'nuggets', name: 'Nuggets', nameAr: 'ناغتس' },
    { key: 'onion-rings', name: 'Onion rings', nameAr: 'حلقات بصل' },
    { key: 'hummus', name: 'Houmous', nameAr: 'حمص' },
    { key: 'soup-starter', name: 'Soupe entrée', nameAr: 'شوربة' }
  ],
  salads: [
    { key: 'green', name: 'Verte', nameAr: 'خضراء' },
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'tuna', name: 'Thon', nameAr: 'تونة' },
    { key: 'cesar', name: 'César', nameAr: 'سيزر' },
    { key: 'nicoise', name: 'Niçoise', nameAr: 'نيسون' },
    { key: 'mixed', name: 'Mixte', nameAr: 'مشكل' }
  ],
  soup: [
    { key: 'vegetable', name: 'Légumes', nameAr: 'خضار' },
    { key: 'chicken', name: 'Poulet', nameAr: 'دجاج' },
    { key: 'fish', name: 'Poisson', nameAr: 'سمك' },
    { key: 'lentil', name: 'Lentilles', nameAr: 'عدس' }
  ],
  sides: [
    { key: 'fries', name: 'Frites', nameAr: 'بطاطس' },
    { key: 'loaded-fries', name: 'Frites garnies', nameAr: 'بطاطس محشية' },
    { key: 'rice', name: 'Riz', nameAr: 'أرز' },
    { key: 'salad', name: 'Salade', nameAr: 'سلطة' },
    { key: 'bread', name: 'Pain', nameAr: 'خبز' },
    { key: 'sauce-portion', name: 'Sauce (portion)', nameAr: 'صلصة حصة' }
  ],
  breakfast: [
    { key: 'eggs', name: 'Œufs', nameAr: 'بيض' },
    { key: 'omelette', name: 'Omelette', nameAr: 'أومليت' },
    { key: 'crepe', name: 'Crêpe', nameAr: 'كريب' },
    { key: 'msemen', name: 'Msemen', nameAr: 'مسمن' },
    { key: 'baghrir', name: 'Baghrir', nameAr: 'بغرير' },
    { key: 'croissant', name: 'Croissant', nameAr: 'كرواسون' },
    { key: 'full-breakfast', name: 'Petit-déj complet', nameAr: 'فطور كامل' }
  ],
  juices: [
    { key: 'orange', name: 'Jus d\'orange', nameAr: 'عصير برتقال' },
    { key: 'mango', name: 'Jus de mangue', nameAr: 'عصير مانجو' },
    { key: 'pineapple', name: 'Jus d\'ananas', nameAr: 'عصير أناناس' },
    { key: 'apple', name: 'Jus de pomme', nameAr: 'عصير تفاح' },
    { key: 'lemon', name: 'Citronnade', nameAr: 'ليمونادة' },
    { key: 'cocktail', name: 'Cocktail de fruits', nameAr: 'كوكتيل فواكه' },
    { key: 'fresh', name: 'Jus frais', nameAr: 'عصير طازج' },
    { key: 'avocado', name: 'Jus d\'avocat', nameAr: 'عصير أفوكادو' },
    { key: 'banana', name: 'Jus de banane', nameAr: 'عصير موز' }
  ],
  'soft-drinks': [
    { key: 'coca-cola', name: 'Coca-Cola', nameAr: 'كوكا كولا' },
    { key: 'pepsi', name: 'Pepsi', nameAr: 'بيبسي' },
    { key: 'fanta', name: 'Fanta', nameAr: 'فانتا' },
    { key: 'sprite', name: 'Sprite', nameAr: 'سبرايت' },
    { key: 'soda', name: 'Soda / Gazeuse', nameAr: 'مشروب غازي' },
    { key: 'energy', name: 'Boisson énergisante', nameAr: 'مشروب طاقة' },
    { key: 'ice-tea', name: 'Ice Tea', nameAr: 'شاي مثلج' }
  ],
  smoothies: [
    { key: 'smoothie', name: 'Smoothie', nameAr: 'سموذي' },
    { key: 'milkshake', name: 'Milkshake', nameAr: 'ميلك شيك' },
    { key: 'chocolate-shake', name: 'Milkshake chocolat', nameAr: 'ميلك شيك شوكولا' },
    { key: 'vanilla-shake', name: 'Milkshake vanille', nameAr: 'ميلك شيك فانيلا' },
    { key: 'strawberry-shake', name: 'Milkshake fraise', nameAr: 'ميلك شيك فراولة' },
    { key: 'lassi', name: 'Lassi', nameAr: 'لاسي' }
  ],
  water: [
    { key: 'still', name: 'Eau plate', nameAr: 'ماء عادي' },
    { key: 'sparkling', name: 'Eau gazeuse', nameAr: 'ماء غازي' },
    { key: 'small', name: 'Petite bouteille', nameAr: 'زجاجة صغيرة' },
    { key: 'large', name: 'Grande bouteille', nameAr: 'زجاجة كبيرة' }
  ],
  'menu-drinks': [
    { key: 'soda', name: 'Soda / Gazeuse', nameAr: 'مشروب غازي' },
    { key: 'juice', name: 'Jus', nameAr: 'عصير' },
    { key: 'water', name: 'Eau', nameAr: 'ماء' },
    { key: 'tea-coffee', name: 'Thé / Café', nameAr: 'شاي / قهوة' },
    { key: 'milkshake', name: 'Milkshake', nameAr: 'ميلك شيك' },
    { key: 'smoothie', name: 'Smoothie', nameAr: 'سموذي' },
    { key: 'lassi', name: 'Lassi', nameAr: 'لاسي' },
    { key: 'hot-drink', name: 'Boisson chaude', nameAr: 'مشروب ساخن' }
  ],
  cafe: [
    { key: 'espresso', name: 'Espresso', nameAr: 'إسبريسو' },
    { key: 'coffee', name: 'Café', nameAr: 'قهوة' },
    { key: 'cappuccino', name: 'Cappuccino', nameAr: 'كابتشينو' },
    { key: 'latte', name: 'Café latte', nameAr: 'لاتيه' },
    { key: 'nescafe', name: 'Nescafé', nameAr: 'نسكافيه' },
    { key: 'tea', name: 'Thé', nameAr: 'شاي' },
    { key: 'mint-tea', name: 'Thé à la menthe', nameAr: 'أتاي بالنعناع' },
    { key: 'green-tea', name: 'Thé vert', nameAr: 'شاي أخضر' },
    { key: 'hot-chocolate', name: 'Chocolat chaud', nameAr: 'شوكولاتة ساخنة' },
    { key: 'coffee-special', name: 'Café spécial', nameAr: 'قهوة خاصة' }
  ],
  desserts: [
    { key: 'ice-cream', name: 'Glace', nameAr: 'آيس كريم' },
    { key: 'cake', name: 'Gâteau', nameAr: 'كعكة' },
    { key: 'pastry', name: 'Pâtisserie', nameAr: 'معجنات' },
    { key: 'fruit', name: 'Fruits', nameAr: 'فواكه' },
    { key: 'tiramisu', name: 'Tiramisu', nameAr: 'تيراميسو' },
    { key: 'crepe-sweet', name: 'Crêpe sucrée', nameAr: 'كريب حلو' },
    { key: 'waffle', name: 'Gaufre', nameAr: 'وافل' }
  ],
  'combo-menu': [
    { key: 'menu-complete', name: 'Menu complet', nameAr: 'وجبة كاملة' },
    { key: 'menu-lunch', name: 'Menu midi', nameAr: 'وجبة غداء' },
    { key: 'menu-kids', name: 'Menu enfant', nameAr: 'وجبة أطفال' },
    { key: 'menu-duo', name: 'Menu duo', nameAr: 'وجبة ثنائية' },
    { key: 'menu-burger', name: 'Menu burger', nameAr: 'وجبة برغر' },
    { key: 'menu-shawarma', name: 'Menu shawarma', nameAr: 'وجبة شاورما' }
  ],
  other: [{ key: 'misc', name: 'Divers', nameAr: 'متنوع' }]
};

export function getRestaurantMenuSections() {
  return RESTAURANT_MENU_SECTIONS;
}

/** Sections groupées pour optgroup (Ajouter / Modifier plat). */
export function getRestaurantMenuSectionsGrouped(language = 'fr') {
  return RESTAURANT_MENU_SECTION_GROUPS.map((group) => ({
    key: group.key,
    label: language === 'ar' ? group.nameAr : group.name,
    sections: RESTAURANT_MENU_SECTIONS.filter((s) => s.group === group.key)
  })).filter((g) => g.sections.length > 0);
}

export function isKnownRestaurantMenuSection(sectionKey) {
  if (!sectionKey) return false;
  return RESTAURANT_MENU_SECTIONS.some((s) => s.key === sectionKey);
}

export function isRestaurantDrinkMenuSection(sectionKey) {
  return RESTAURANT_DRINK_SECTION_KEYS.has(sectionKey);
}

export function isKnownRestaurantMenuType(sectionKey, typeKey) {
  if (!typeKey) return true;
  return getTypesForRestaurantMenuSection(sectionKey).some((t) => t.key === typeKey);
}

export function getRestaurantMenuSectionLabel(sectionKey, language = 'fr') {
  const section = RESTAURANT_MENU_SECTIONS.find((s) => s.key === sectionKey);
  if (!section) return sectionKey || '';
  return language === 'ar' ? section.nameAr : section.name;
}

export function getTypesForRestaurantMenuSection(sectionKey) {
  return RESTAURANT_MENU_TYPES_BY_SECTION[sectionKey] || [];
}

export function getTypeLabelForRestaurantMenu(sectionKey, typeKey, language = 'fr') {
  const types = getTypesForRestaurantMenuSection(sectionKey);
  const t = types.find((x) => x.key === typeKey);
  if (!t) return typeKey || '';
  return language === 'ar' ? t.nameAr : t.name;
}

/** Trim free-text section/type; empty → ''. */
export function normalizeManualMenuLabel(value) {
  return String(value || '').trim();
}

/** Normalise anciens plats (subcategory restaurant-menu legacy). */
export function normalizeRestaurantDishSection(product) {
  const sub = product?.subcategory;
  if (!sub || sub === 'restaurant-menu') return product?.menuSection || 'other';
  return sub;
}

export function buildRestaurantMenuCategoryTree() {
  const subcategories = {};
  for (const section of RESTAURANT_MENU_SECTIONS) {
    subcategories[section.key] = {
      name: section.name,
      nameAr: section.nameAr,
      types: RESTAURANT_MENU_TYPES_BY_SECTION[section.key] || []
    };
  }
  return {
    name: 'Menu Restaurant',
    nameAr: 'قائمة المطعم',
    subcategories
  };
}
